import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Copy, Trash2, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  useGetWebhookCredentialsQuery,
  useCreateWebhookCredentialMutation,
  useUpdateWebhookCredentialMutation,
  useDeleteWebhookCredentialMutation,
} from "@/store/rest_api";
import DialerTreeConfig from "@/app/config";

interface WebhookCredentialsManagerProps {
  workflowId: string;
}

const ingestUrl = `${DialerTreeConfig.baseAPIEndpoint}/v1/webhooks/in`;

const WebhookCredentialsManager: React.FC<WebhookCredentialsManagerProps> = ({ workflowId }) => {
  const { data: credentials = [], isLoading } = useGetWebhookCredentialsQuery(workflowId);
  const [createCredential, { isLoading: isCreating }] = useCreateWebhookCredentialMutation();
  const [updateCredential] = useUpdateWebhookCredentialMutation();
  const [deleteCredential] = useDeleteWebhookCredentialMutation();
  const [newCredentialName, setNewCredentialName] = useState("");

  const handleMint = async () => {
    try {
      await createCredential({ workflowId, name: newCredentialName.trim() || "Default" }).unwrap();
      setNewCredentialName("");
      toast.success("Token minted");
    } catch (mintError: any) {
      toast.error(mintError?.data?.detail || "Failed to mint token");
    }
  };

  const handleToggle = async (credentialId: string, enabled: boolean) => {
    try {
      await updateCredential({ workflowId, credentialId, enabled }).unwrap();
    } catch (updateError: any) {
      toast.error(updateError?.data?.detail || "Failed to update token");
    }
  };

  const handleDelete = async (credentialId: string) => {
    if (!confirm("Revoke this token? Anyone using it will lose access immediately.")) {
      return;
    }
    try {
      await deleteCredential({ workflowId, credentialId }).unwrap();
      toast.success("Token revoked");
    } catch (deleteError: any) {
      toast.error(deleteError?.data?.detail || "Failed to revoke token");
    }
  };

  const copyToClipboard = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-4 border rounded-md p-3 bg-muted/20">
      <div>
        <Label className="text-xs">Ingest URL</Label>
        <div className="flex gap-2 mt-1">
          <Input value={ingestUrl} readOnly className="font-mono text-xs" />
          <Button type="button" variant="outline" size="sm" onClick={() => copyToClipboard(ingestUrl, "URL")}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          POST any JSON. Send <code>Authorization: Bearer &lt;token&gt;</code>. We extract phone, name, email
          (top-level or under <code>lead</code>/<code>contact</code>/<code>data</code>); everything else is kept as
          custom fields.
        </p>
      </div>

      <div>
        <Label className="text-xs">Tokens</Label>
        {isLoading ? (
          <p className="text-xs text-muted-foreground py-2">Loading...</p>
        ) : credentials.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">No tokens yet. Mint one below.</p>
        ) : (
          <div className="space-y-2 mt-1">
            {credentials.map((credential) => (
              <div key={credential.id} className="flex items-center gap-2 text-sm">
                <Switch
                  checked={credential.enabled}
                  onCheckedChange={(checked) => handleToggle(credential.id, checked)}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{credential.name}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate">{credential.token}</div>
                  {credential.lastUsedAt && (
                    <div className="text-[10px] text-muted-foreground">
                      Last used {new Date(credential.lastUsedAt).toLocaleString()}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(credential.token, "Token")}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="ghost" size="sm" title="Options">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(credential.id)}>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete credential
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <Input
            placeholder="Token label (e.g. Zapier)"
            value={newCredentialName}
            onChange={(changeEvent) => setNewCredentialName(changeEvent.target.value)}
          />
          <Button type="button" variant="outline" size="sm" onClick={handleMint} disabled={isCreating}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Mint
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WebhookCredentialsManager;
